import React, { useState, useEffect } from 'react';
import { Sidebar } from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface MainLayoutProps {
    children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        if (typeof window === 'undefined') return true;
        const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return stored === null ? true : stored === '1';
    });

    const toggleSidebar = () => setSidebarOpen(prev => !prev);
    const toggleCollapsed = () => {
        setCollapsed(prev => {
            const next = !prev;
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
            }
            return next;
        });
    };

    useEffect(() => {
        const handleToggle = (e: any) => setSidebarOpen(e.detail);
        window.addEventListener('app-sidebar-toggle', handleToggle);
        return () => window.removeEventListener('app-sidebar-toggle', handleToggle);
    }, []);

    return (
        <div className="h-screen relative overflow-hidden bg-background text-foreground">
            <a
                href="#contenu-principal"
                className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow"
            >
                Aller au contenu
            </a>

            {/* Content */}
            <div className="relative z-10 flex h-full">
                <Sidebar
                    isOpen={sidebarOpen}
                    toggleSidebar={toggleSidebar}
                    collapsed={collapsed}
                    toggleCollapsed={toggleCollapsed}
                />

                <div className={cn(
                    "flex-1 flex flex-col transition-[margin] duration-200 ease-out min-h-0",
                    "pb-16 md:pb-0",
                    sidebarOpen ? (collapsed ? "md:ml-[64px]" : "md:ml-[220px]") : "md:ml-0"
                )}>
                    <main
                        id="contenu-principal"
                        tabIndex={-1}
                        className="flex-1 px-4 py-4 md:px-6 md:py-5 lg:px-8 overflow-auto custom-scrollbar focus:outline-none"
                    >
                        {/* Transition brève, sans déplacement : elle signale le
                            changement de page sans retarder la lecture. */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={location.pathname}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.12 }}
                                className="w-full min-w-0 max-w-[1600px] mx-auto"
                            >
                                {children}
                            </motion.div>
                        </AnimatePresence>
                    </main>
                </div>
            </div>
        </div>
    );
};
